import { parseCsvBooleanStrict, parseOptionalNumber } from '../../domain/csv/csvParseHelpers';
import { resolveMasterFromRecordFields, type MasterMatchCandidate } from '../../domain/csv/masterMatching';
import { createBrand, updateBrand } from '../brandsRepo';
import type { BrandRecord } from '../brandsRepo';
import {
  brandCsvWouldChange,
  loadBrandCandidates,
  loadBrandMap,
  previewRow,
  type CsvPreviewRowOutcome,
  type ParsedRow,
} from './catalogCsvImportHelpers';

export type BrandCsvPreviewContext = {
  brandMasterCandidates: MasterMatchCandidate[];
  brandFullMap: Map<string, BrandRecord>;
};

export async function loadBrandCsvPreviewContext(): Promise<BrandCsvPreviewContext> {
  const brandMasterCandidates = await loadBrandCandidates();
  const brandFullMap = await loadBrandMap();
  return { brandMasterCandidates, brandFullMap };
}

export function previewBrandCsvRow(
  rowNumber: number,
  record: ParsedRow,
  ctx: BrandCsvPreviewContext
): CsvPreviewRowOutcome {
  const nombre = record.nombre?.trim();
  if (!nombre) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', 'Sin nombre', 'Ingresá nombre.'),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const match = resolveMasterFromRecordFields(ctx.brandMasterCandidates, {
    id: record.marca_id,
    label: nombre,
  });
  if (match.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', nombre, match.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', nombre, activoCell.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  if (match.id) {
    const existing = ctx.brandFullMap.get(match.id);
    if (existing && !brandCsvWouldChange(existing, record)) {
      return {
        row: previewRow(rowNumber, 'skip', 'omitir', nombre),
        okCount: 0,
        skipCount: 1,
        errorCount: 0,
        newProductCount: 0,
      };
    }
  }
  return {
    row: previewRow(rowNumber, 'ok', match.id ? 'actualizar' : 'crear', nombre),
    okCount: 1,
    skipCount: 0,
    errorCount: 0,
    newProductCount: 0,
  };
}

export async function commitBrandRow(
  record: ParsedRow,
  preloadedCandidates?: MasterMatchCandidate[]
): Promise<'created' | 'updated'> {
  const nombre = record.nombre?.trim();
  if (!nombre) throw new Error('Nombre obligatorio.');
  const candidates = preloadedCandidates ?? await loadBrandCandidates();
  const match = resolveMasterFromRecordFields(candidates, { id: record.marca_id, label: nombre });
  if (match.error) throw new Error(match.error);

  const markup = parseOptionalNumber(record.markup_default);
  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) throw new Error(activoCell.error);
  const isActive = activoCell.value;

  if (match.id) {
    const existing = (await loadBrandMap()).get(match.id);
    await updateBrand({
      id: match.id,
      name: nombre,
      code: record.codigo?.trim() || null,
      website: record.sitio_web?.trim() || null,
      defaultMarkupPercent: markup ?? null,
      isActive: isActive ?? existing?.isActive ?? true,
    });
    return 'updated';
  }

  // reuseExisting: si el nombre ya existe (carrera o normalización distinta al
  // match previo), el CSV enlaza al id existente en vez de fallar la fila.
  await createBrand(
    {
      name: nombre,
      code: record.codigo?.trim() || null,
      website: record.sitio_web?.trim() || null,
      defaultMarkupPercent: markup ?? null,
      isActive: isActive ?? true,
    },
    { reuseExisting: true },
  );
  return 'created';
}
